import classNames from "classnames";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import { useValidate } from "formvalidation/hooks";
import moment from "moment";
import React from "react";
import { useViewport } from "utilities/hookutils";
import { formatTimeOfPerformance, getRealStartEndDate, getShowTimeDifferenceLabel, parseTimeOfPerformance } from "utilities/showDateUtils";
import { isViewport } from "utilities/viewportUtils";
import AllDayDatePickers from "./AllDayDatePickers";
import { type ShowDates } from "./models";
import NotAllDayDatePickers from "./NotAllDayDatePickers";
import { areShowDatesValid, areDatesTooFarApart, isAfterMaxYear, isBeforeMinYear, isStartAfterEnd } from "./utils";

export interface Props {
  dates: ShowDates
  onDatesChanged: (newDates: ShowDates, hasErrors: boolean) => void
  validatorKey?: string
  compact?: boolean
}

const is24Hr = site.culture.is24Hr;

const timeFormat = is24Hr ? "HH:mm" : "hh:mm A";

const getDayInMonth = (date: moment.Moment) => moment.utc(date).date();
const getDayName = (date: moment.Moment) => serverReference.dayNames[moment.utc(date).day()];
const getMonthName = (date: moment.Moment) => serverReference.monthNames[moment.utc(date).month()];
const formatDate = (date: moment.Moment) => `${getDayName(date)}, ${getDayInMonth(date)} ${getMonthName(date)}`;
const formatTime = (date: moment.Moment) => date.format(timeFormat);

const ShowDatePicker = (props: Props) => {
  const { dates } = props;
  const { startDisplayDate, endDisplayDate, mode } = dates;

  const onDatesChange = (showDates: ShowDates) => {
    const valid = areShowDatesValid(showDates);
    props.onDatesChanged(showDates, !valid);
  };

  const getDateInfo = (): string => {
    const { startRealDate, endRealDate } = getRealStartEndDate(props.dates);

    const sameDayEvent = startRealDate.clone().startOf("day").isSame(endRealDate.clone().startOf("day"));
    const sameTime = startRealDate.isSame(endRealDate);

    const appointmentStartReal = formatDate(startRealDate);
    const appointmentStartDisplay = formatDate(startDisplayDate);
    const appointmentEndReal = formatDate(endRealDate);
    const appointmentEndDisplay = formatDate(endDisplayDate);

    const appointmentStartTimeDisplay = formatTime(startDisplayDate);
    const appointmentEndTimeDisplay = formatTime(endDisplayDate);

    const tooBigDateDifference = areDatesTooFarApart(startDisplayDate, endDisplayDate);

    if (isStartAfterEnd(mode === "allDay", startRealDate, endRealDate)) {
      return `<span class="s1_red">${RESX.DateTime.TheEndDateIsBeforeTheBeginDate}</span>`;
    }

    if (tooBigDateDifference) {
      return `<span class="s1_red">${RESX.DateTime.DateDifferenceTooBig}</span>`;
    }

    if (isBeforeMinYear(startRealDate)) {
      return `<span className="s1_red">${RESX.DateTime.ChooseDateAfterX.replace("{0}", serverReference.constants.appointment.minYear.toString())}</span>`;
    }

    if (isAfterMaxYear(endRealDate)) {
      return `<span className="s1_red">${RESX.DateTime.ChooseDateBeforeX.replace("{0}", serverReference.constants.appointment.maxYear.toString())}</span>`;
    }

    if (isAllDay && startDisplayDate.isSame(endDisplayDate)) {
      return `${RESX.DateTime.ThisAppointmentTakesPlace} <b>${appointmentStartDisplay}</b>`;
    }

    if (isAllDay) {
      return `${RESX.DateTime.ThisAppointmentTakesPlace} <b>${appointmentStartDisplay}</b> ${RESX.DateTime.UntilAndIncluding} <b>${appointmentEndDisplay}</b>`;
    }

    if (mode === "hasTimeStartsInNight" && sameDayEvent) {
      const duration = sameTime
        ? `${appointmentStartReal} ${appointmentStartTimeDisplay}`
        : `${appointmentStartReal} ${appointmentStartTimeDisplay} - ${appointmentEndTimeDisplay}`;

      return RESX.DateTime.ThisAppointmentBeginsInTheNight.replace("{0}", appointmentStartDisplay).replace("{1}", duration);
    }

    if (mode === "hasTimeStartsInNight" && !sameDayEvent) {
      const duration = `${appointmentStartReal} ${appointmentStartTimeDisplay} - ${appointmentEndReal} ${appointmentEndTimeDisplay}`;

      return RESX.DateTime.ThisAppointmentBeginsInTheNight.replace("{0}", appointmentStartDisplay).replace("{1}", duration);
    }

    if (sameDayEvent) {
      const duration = sameTime
        ? `${appointmentStartDisplay} ${appointmentStartTimeDisplay}`
        : `${appointmentStartDisplay} ${appointmentStartTimeDisplay} - ${appointmentEndTimeDisplay}`;

      return `${RESX.DateTime.ThisAppointmentTakesPlace} <b>${duration}</b>`;
    }

    if (!sameDayEvent) {
      const duration = `${appointmentStartDisplay} ${appointmentStartTimeDisplay} - ${appointmentEndDisplay} ${appointmentEndTimeDisplay}`;
      return `${RESX.DateTime.ThisAppointmentTakesPlace} <b>${duration}</b>`;
    }

    throw new Error("Invalid state for the show date picker");
  };

  const onIsAllDayChanged = (isAllDay: boolean) => {
    if (isAllDay) {
      onDatesChange({
        startDisplayDate: startDisplayDate.clone().startOf("day"),
        endDisplayDate: endDisplayDate.clone().startOf("day"),
        timeOfPerformance: dates.timeOfPerformance,
        mode: "allDay"
      });
    } else {
      const parsedTimeOfPerformance = parseTimeOfPerformance(dates);
      onDatesChange({
        startDisplayDate: parsedTimeOfPerformance.displayStart,
        endDisplayDate: parsedTimeOfPerformance.displayEnd,
        timeOfPerformance: formatTimeOfPerformance(parsedTimeOfPerformance.displayStart, parsedTimeOfPerformance.displayEnd),
        mode: parsedTimeOfPerformance.mode
      });
    }
  };

  const viewport = useViewport();

  const isWidescreen = isViewport(viewport).biggerThan("desktopMedium");
  const datepickerClasses = classNames("margin-bottom-small gap-small", {
    "flex align-center": isWidescreen && !props.compact,
    "flex-column": !isWidescreen || props.compact
  });

  const isAllDay = mode === "allDay";

  const validation = useValidate({
    key: props.validatorKey,
    title: RESX.Show.DateOfEvent,
    value: props.dates,
    validate: async (v) => areShowDatesValid(v)
  })

  let timeDifferenceLabel = getShowTimeDifferenceLabel(dates);
  if (timeDifferenceLabel) timeDifferenceLabel = `(${timeDifferenceLabel})`;

  return <div ref={validation.ref}>
    <div className="margin-bottom-small">
      <Checkbox
        label={RESX.DateTime.AllDay}
        checked={isAllDay}
        onChange={() => { onIsAllDayChanged(!isAllDay); }}
      />
    </div>
    <div className={datepickerClasses}>
      {isAllDay && <AllDayDatePickers
        onDatesChange={onDatesChange}
        showDates={dates}
      />}
      {!isAllDay && <NotAllDayDatePickers
        onDatesChange={onDatesChange}
        showDates={dates}
      />}
    </div>
    <div>
      {/* We set HTML, because the translations contain <b></b> tags for partial bolding within a single string */}
      <span id="showcopy_dateinfo" className="s1_small margin-right-small" dangerouslySetInnerHTML={{ __html: getDateInfo() }}></span>
      <span id="showcopy_timespan" className="text-sublabel">{timeDifferenceLabel}</span>
    </div>
  </div>;
};

export default ShowDatePicker;